import React from 'react';
import ReactDOM from 'react-dom';
import {
  BrowserRouter as Router,
  Route,
  Switch,
} from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import {
  Auth,
  Monitor,
  Management,
  Profile,
  Project,
  Finance,
  Customer,
  Director,
  Humen,
  Manpower,
  Sale,
  Accounting,
  Marketing,
} from './layouts';
import configureStore from './redux/configureStore';
// import Home from '../iews/Home';

import PrivateRoute from './contexts/PrivateRoute';
import AuthProvider from './contexts/AuthContext';
import SocketProvider from './contexts/SocketContext';
import { AnnouncementProvider } from './contexts/AnnouncementContext';
import { AiChatPanelProvider } from './contexts/AiChatPanelContext';
import { EmployeeEditProvider } from './contexts/EmployeeEditContext';
import { buildTheme } from './theme';
import { loadOrg } from './utils/orgConfig';

import '@fortawesome/fontawesome-free/css/all.min.css';
import './assets/styles/index.css';
import Home from './views/Home';
import OnboardingForm from './views/Onboarding/OnboardingForm';
import ProjectPopupWindow from './views/Project/ProjectPopupWindow';
import OpportunityPopupWindow from './views/Marketing/OpportunityPopupWindow';
import AuthorizeConsent from './views/OAuth/AuthorizeConsent';
import SetupAdmin from './views/Setup/SetupAdmin';
import { NotFound } from './components/Error';
import DevNoticePopup from './components/DevNoticePopup';
import DevPageBadge from './components/DevPageBadge';
import AiChatPanel from './components/AiChatPanel';
import GlobalErrorDialog from './components/GlobalErrorDialog';
import GlobalAlertDialog from './components/GlobalAlertDialog';
import GlobalConfirmDialog from './components/GlobalConfirmDialog';
import GlobalErrorBoundary from './components/GlobalErrorBoundary';
import WindowManager from './components/Card/WindowManager';
import PushNotificationSetup from './components/Notification/PushNotificationSetup';

const store = configureStore();

if (process.env.NODE_ENV === 'development') {
  document.body.classList.add('dev-mode');

  const favicon = document.getElementById('favicon');
  if (favicon) {
    favicon.href = `${process.env.PUBLIC_URL}/faviconDev.ico`;
  }

  document.title = `${document.title} (DEV)`;
}

const renderApp = (theme) => ReactDOM.render(
  <GlobalErrorBoundary>
    <Router>
      <Provider store={store}>
        <AuthProvider>
          <SocketProvider>
            <ThemeProvider theme={theme}>
              <EmployeeEditProvider>
              <DevNoticePopup />
              <DevPageBadge />
              <GlobalErrorDialog />
              <GlobalAlertDialog />
              <GlobalConfirmDialog />
              <WindowManager />
              <div className={process.env.NODE_ENV === 'development' ? 'bg-yellow-50' : 'bg-gray-100'}>
                <Switch>
                  <Route path="/join/:token" component={OnboardingForm} />
                  <Route path="/auth" component={Auth} />
                  <Route path="/manpower" component={Manpower} />
                  <Route path="/oauth/authorize" component={AuthorizeConsent} />
                  <Route path="/setup-admin" component={SetupAdmin} />
                  <PrivateRoute>
                    <PushNotificationSetup />
                    {/* PrivateRoute เป็น <Route> ที่ไม่มี path (matches ทุก location เสมอ) —
                        <Switch> ด้านนอกสุดเลยเลือก PrivateRoute นี้ก่อนตลอดสำหรับ path ใดๆ ที่ไม่ตรง
                        4 route แรก (join/auth/manpower/oauth) ทำให้ <Redirect from="*"> เดิมที่อยู่
                        หลัง PrivateRoute ใน Switch นอกกลายเป็น dead code (ไม่มีทางถูกเรียกถึงเลย) —
                        ต้องมี <Switch>+catch-all อยู่ "ข้างใน" children ของ PrivateRoute เอง ถึงจะดัก
                        URL ที่ login แล้วแต่ไม่ตรง route โมดูลไหนเลยได้จริง (เดิม path พวกนี้ไม่ขึ้น
                        อะไรเลย เพราะไม่มี Route ไหนใน children match แล้วก็ไม่มี Switch ให้ fallback) */}
                    <AnnouncementProvider>
                    <AiChatPanelProvider>
                    <AiChatPanel />
                    <Switch>
                      <Route exact path="/" component={Home} />
                      <Route path="/profile" component={Profile} />
                      <Route path="/management" component={Management} />
                      <Route path="/monitor" component={Monitor} />
                      {/* ต้องอยู่ก่อน /project เพราะ react-router v5 จับ prefix — ไม่งั้น
                          /project-popup/:id จะโดน /project (layout เต็ม) ตักไปก่อน */}
                      <Route
                        path="/project-popup/:id"
                        component={ProjectPopupWindow}
                      />
                      <Route path="/project" component={Project} />
                      <Route path="/finance" component={Finance} />
                      <Route path="/accounting" component={Accounting} />
                      <Route path="/customer" component={Customer} />
                      <Route path="/director" component={Director} />
                      <Route path="/humen" component={Humen} />
                      <Route path="/sale" component={Sale} />
                      {/* ต้องอยู่ก่อน /marketing เพราะ react-router v5 จับ prefix — ไม่งั้น
                          /marketing-popup/:id จะโดน /marketing (layout เต็ม) ตักไปก่อน */}
                      <Route
                        path="/marketing-popup/:id"
                        component={OpportunityPopupWindow}
                      />
                      <Route path="/marketing" component={Marketing} />
                      <Route path="*" component={NotFound} />
                    </Switch>
                    </AiChatPanelProvider>
                    </AnnouncementProvider>
                  </PrivateRoute>
                </Switch>
              </div>
              </EmployeeEditProvider>
            </ThemeProvider>
          </SocketProvider>
        </AuthProvider>
      </Provider>
    </Router>
  </GlobalErrorBoundary>,
  document.getElementById('root'),
);

// โหลดค่าเฉพาะองค์กร (/org/theme.json, /org/config.json) ก่อน render เพื่อไม่ให้สีกระพริบเปลี่ยนทีหลัง —
// loadOrg ไม่ throw (ไฟล์ไม่มี/เสีย = ใช้ค่าเริ่มต้น) และรอไม่เกิน 3 วินาที
loadOrg().then((org) => {
  if (org.brand.title) {
    document.title = process.env.NODE_ENV === 'development' ? `${org.brand.title} (DEV)` : org.brand.title;
  }

  // ไอคอนเฉพาะองค์กร (org/icon.png) แทน favicon บนแท็บ — ไม่มีไฟล์ = ไอคอนเดิม
  if (org.iconUrl) {
    const favicon = document.getElementById('favicon');
    if (favicon) {
      favicon.type = 'image/png';
      favicon.href = org.iconUrl;
    }
  }
  // ไอคอนติดตั้งแอปบนหน้าจอหลัก (iPhone): org/app-icon.png (ไฟล์แยก) ถ้าไม่มีใช้ org/icon.png เหมือน favicon
  const installIconUrl = org.appIconUrl || org.iconUrl;
  if (installIconUrl) {
    const touchIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (touchIcon) {
      touchIcon.href = installIconUrl;
    }
  }

  renderApp(buildTheme(org.theme));
});
